import type { FundSymbol } from '../products'

import {
  type LimitOrderLimits,
  type LimitOrderTickUsage,
  type LimitOrderType,
  useGetLimitOrderPaused,
  useGetLimitOrders,
  useGetLimits,
  useGetTickUsage,
} from './limit_orders_block_queries'

export function useGetMatchingLimitOrder(args: {
  fund_symbol: FundSymbol
  price: number
  tick: number
}): LimitOrderType | undefined {
  const { data: user_orders } = useGetLimitOrders(args)

  const matching = user_orders?.find((order) => order.tick == args.tick)

  return matching
}

// eslint-disable-next-line complexity, max-lines-per-function
export function check_disabled({
  paused,
  limits,
  user_orders,
  per_tick_usage,
}: {
  paused?: boolean
  limits?: LimitOrderLimits
  user_orders?: LimitOrderType[]
  per_tick_usage?: LimitOrderTickUsage
}): {
  isSuccess: boolean
  disabled: boolean | 'temporary'
  reason: string
  maxes: {
    max_tokens_per_order: number
    max_tokens_per_tick: number
  }
} {
  if (paused == null || limits == null || per_tick_usage == null) {
    return {
      isSuccess: false,
      disabled: paused == true,
      reason: paused == true ? `Contract paused` : '',
      maxes: {
        max_tokens_per_order: 0,
        max_tokens_per_tick: 0,
      },
    }
  }
  const maxes = per_tick_usage.sell_symbol == 'USDC' ? limits.usdc : limits.fund

  if (paused) {
    return {
      isSuccess: true,
      disabled: true,
      reason: `Contract paused`,
      maxes,
    }
  }

  if ((user_orders?.length || 0) >= Number(limits.max_orders_count_per_user)) {
    return {
      isSuccess: true,
      disabled: true,
      reason: 'Your max orders reached',
      maxes,
    }
  }

  if (per_tick_usage.count >= limits.max_orders_count_per_tick) {
    return {
      isSuccess: true,
      disabled: true,
      reason: 'Max orders for this price reached',
      maxes,
    }
  }

  if (per_tick_usage.tokens >= maxes.max_tokens_per_tick) {
    return {
      isSuccess: true,
      disabled: true,
      reason: 'Max tokens for this price reached',
      maxes,
    }
  }

  return {
    isSuccess: true,
    disabled: false,
    reason: '',
    maxes,
  }
}

// eslint-disable-next-line complexity
export function useGetLimitOrderDisabled(args: {
  fund_symbol: FundSymbol
  price: number
  tick: number
}) {
  const { data: paused } = useGetLimitOrderPaused(args)
  const { data: limits } = useGetLimits(args)
  const { data: user_orders } = useGetLimitOrders(args)
  const { data: per_tick_usage } = useGetTickUsage(args)

  return check_disabled({ paused, limits, user_orders, per_tick_usage })
}
